import { usersApi } from "#/api/usersApi";
import { type User } from "#/types/user";
import { Button, Modal, type ModalProps } from "@mantine/core";
import { IconTrash } from "@tabler/icons-react";
import { useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { usePermission } from "#/hooks/usePermission";
import PermissionTooltip from "#/components/ui/PermissionTooltip";

export interface Props extends ModalProps {
  user: User;
  onSuccess: () => void;
}

const DeleteClientUserModal = ({ user, onSuccess, ...props }: Props) => {
  const { t } = useTranslation();
  const { canDelete } = usePermission();
  const [removing, setRemoving] = useState<boolean>(false);

  const removeUser = useMutation({
    mutationFn: async () => {
      setRemoving(true);
      return await usersApi.delete(user.id);
    },
    onSuccess: () => {
      setRemoving(false);
      onSuccess();
    },
    onError: (error) => {
      console.log("🚀 ~ DeleteClientUserModal ~ error:", error);
      setRemoving(false);
    },
  });

  return (
    <Modal
      {...props}
      centered
      title={t("clients.users.deactivateUser")}
      closeOnClickOutside={false}
      onClose={() => {
        props.onClose();
      }}
      classNames={{
        title: "font-bold",
      }}
    >
      <div className="w-full flex flex-col items-center justify-center gap-4">
        <div className="flex flex-col gap-2 text-sm">
          <span>{t("clients.users.deactivateDescription")}</span>
          <div className="flex flex-col gap-1 mx-auto  my-4 bg-slate-100 border border-dashed border-slate-400 rounded-lg p-4 w-full text-center">
            <span className="font-bold">{user?.name ?? "--"}</span>
            <span>{user?.email ?? "--"}</span>
          </div>
          <span>
            {t("clients.users.deactivateWarning")}
          </span>

          <span className="font-bold my-2">
            {t("clients.users.deactivateConfirmation")}
          </span>
        </div>

        <div className="w-full grid grid-cols-[auto_1fr] items-center gap-2">
          <Button size="sm" color="black" onClick={() => props.onClose()}>
            {t("common.abort")}
          </Button>
          <PermissionTooltip disabled={!canDelete()}>
            <Button
              onClick={() => removeUser.mutate()}
              color="red"
              size="sm"
              loading={removing}
              leftSection={<IconTrash size={18} />}
              disabled={!canDelete()}
            >
              {t("clients.users.deactivateButton")}
            </Button>
          </PermissionTooltip>
        </div>
      </div>
    </Modal>
  );
};

export default DeleteClientUserModal;
